/* The welcome for brand-new people (owner request 2026-10-15): pure parts, tested in tests/app/welcome.test.ts.
   - when to show it (welcomeDue), and the guide's three steps (guideSteps);
   - where a tooltip bubble goes next to the thing it points at (placeTip): below or above, kept on screen, else centred. */

export interface Box { left: number; top: number; width: number; height: number }
export interface Place { left: number; top: number; side: 'below' | 'above' | 'center'; arrowX: number }

/** the welcome is due for this person on this device: may record time, not yet welcomed here, nothing of theirs on this
    device (no entry, no running timer, nothing waiting to be sent or kept as Not saved). The server is asked after this. */
export function welcomeDue(o: {canTrack: boolean; welcomedHere: boolean; ownEntries: number; ownTimer: boolean; pending: number; notSaved: number}): boolean {
  return o.canTrack && !o.welcomedHere && o.ownEntries === 0 && !o.ownTimer && o.pending === 0 && o.notSaved === 0;
}

export interface Step { target: string[]; text: string; last: boolean }
export const STEP_TEXT = [
  'What are you working on? Type it here, or pick the client and project.',
  'Tap ▶ to start. The clock starts counting.',
  'Tap ■ when you’re done — it’s saved. Forgot to start? Use “+ Add time by hand”.',
];
export const NO_PROJECTS = "You haven't been added to any projects yet. Ask the owner to add you to one, then come back.";
/** the guide's steps; `target` lists element ids (the first one found is pointed at; all found are lit up). A member or
    the bookkeeper with no assigned project gets one step: ask the owner. */
export function guideSteps(noProjects: boolean): Step[] {
  const first = ['timerDesc', 'timerClient', 'timerProject'];
  if (noProjects) return [{target: first, text: NO_PROJECTS, last: true}];
  return [
    {target: first, text: STEP_TEXT[0], last: false},
    {target: ['timerToggleBtn'], text: STEP_TEXT[1], last: false},
    {target: ['openManualEntryBtn', 'timerToggleBtn'], text: STEP_TEXT[2], last: true},
  ];
}

/** the smallest box around all of these (null when none) */
export function union(boxes: Box[]): Box | null {
  if (!boxes.length) return null;
  const l = Math.min(...boxes.map(b => b.left)), t = Math.min(...boxes.map(b => b.top));
  const r = Math.max(...boxes.map(b => b.left + b.width)), btm = Math.max(...boxes.map(b => b.top + b.height));
  return {left: l, top: t, width: r - l, height: btm - t};
}

const GAP = 12, EDGE = 10;
/** where the bubble (w × h) goes for a lit-up area `lit` (`at` = the element the arrow points at, inside it), in a
    viewport vw × vh whose top `topBar` and bottom `bottomBar` pixels are covered by the app's bars: below the area if it
    fits, else above, else next to the element itself, else centred. Always fully on screen. */
export function placeTip(lit: Box | null, at: Box | null, w: number, h: number, vw: number, vh: number, topBar = 0, bottomBar = 0): Place {
  const minTop = Math.max(EDGE, topBar), maxBottom = vh - Math.max(EDGE, bottomBar);
  const centre = (): Place => ({left: Math.max(EDGE, (vw - w) / 2), top: Math.max(EDGE, (vh - h) / 2), side: 'center', arrowX: w / 2});
  if (!lit || !at) return centre();
  const left = Math.min(Math.max(EDGE, at.left + at.width / 2 - w / 2), Math.max(EDGE, vw - w - EDGE));
  const arrowX = Math.min(Math.max(18, at.left + at.width / 2 - left), w - 18);
  for (const b of [lit, at]) {
    const below = b.top + b.height + GAP, above = b.top - GAP - h;
    if (below >= minTop && below + h <= maxBottom) return {left, top: below, side: 'below', arrowX};
    if (above >= minTop && above + h <= maxBottom) return {left, top: above, side: 'above', arrowX};
  }
  return centre();
}
